import { useCallback, useState } from 'react';

// Card layout's AC (NA-5529, AC #4): show the first 10 records on open.
const INITIAL_LIMIT = 10;

export type UseJournalRemainingResult<T> = {
  data: T[];
  isExpanded: boolean;
  showRemaining: () => void;
  collapse: () => void;
};

/**
 * "Show first 10 + 残りをすべて表示" for the Journal card layout.
 *
 * Unlike Transactions' equivalent (`useApprovingInvoiceReportTransactionsPaged`),
 * `useJournalEntries` already fetches every row up front, so this only slices
 * what is already in memory — no background fetch chain or loading state.
 */
export const useJournalRemaining = <T>(
  allData: T[],
  reportId: string
): UseJournalRemainingResult<T> => {
  const [isExpanded, setIsExpanded] = useState(false);

  // The drawer swaps `reportId` on the same mounted component rather than
  // remounting it, so the expand/collapse choice must reset per report.
  // Reset synchronously during render (not a `useEffect`) so there's no
  // stale-expanded flash. Mirrors `useApprovingInvoiceReportTransactionsPaged`.
  const [seenReportId, setSeenReportId] = useState(reportId);
  if (reportId !== seenReportId) {
    setSeenReportId(reportId);
    setIsExpanded(false);
  }

  const showRemaining = useCallback(() => setIsExpanded(true), []);
  const collapse = useCallback(() => setIsExpanded(false), []);

  const data = isExpanded ? allData : allData.slice(0, INITIAL_LIMIT);

  return { data, isExpanded, showRemaining, collapse };
};
